import { useState } from "react";

import DotsHorizontal from "@uui/DotsHorizontal";
import EditIcon from "@uui/Edit02";
import TrashIcon from "@uui/Trash02";
import { useNavigate, useParams } from "react-router-dom";

import Divider from "@mui/material/Divider";

import DeleteMeetingReportConfirmationModal from "@app/analytics/components/reports/DeleteMeetingReportConfirmationModal";
import HeaderButton from "@app/analytics/components/reports/HeaderButton";
import RemoveMeetingReportConfirmationModal from "@app/analytics/components/reports/RemoveMeetingReportConfirmationModal";
import useDeleteReport from "@app/analytics/MeetingReport/hooks/useDeleteReport";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import ROUTES from "@app/common/routes";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";

const ReportActionsMenu = ({ isMeetingOwner }: { isMeetingOwner: boolean }) => {
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const { sessionId } = useParams();
  const navigate = useNavigate();
  const {
    anchorElement,
    onClick,
    onClose: onMenuClose,
    open,
  } = useAnchorElement();

  const { deleteReport, removeReport } = useDeleteReport();
  const canRenameReport = useEditStore(
    (state) => state.editAccessLevel === "full"
  );

  const openModal = () => {
    onMenuClose();
    setConfirmationModalOpen(true);
  };

  const closeModal = () => setConfirmationModalOpen(false);

  const handleEditReportTitle = () => {
    onMenuClose();
    const element = document.getElementById("meeting-report-title");
    element?.click();
  };

  const handleDeleteReport = () =>
    deleteReport.mutate(sessionId!, {
      onSuccess: () => navigate(ROUTES.MEETING_REPORTS),
      onError: closeModal,
    });

  const handleRemoveReport = () =>
    removeReport.mutate(sessionId!, {
      onSuccess: () => navigate(ROUTES.MEETING_REPORTS),
      onError: closeModal,
    });

  return (
    <>
      <HeaderButton
        className="meeting-report__header__actions"
        Icon={<DotsHorizontal />}
        onClick={onClick}
      />
      <Menu anchorEl={anchorElement} open={open} onClose={onMenuClose}>
        {canRenameReport && (
          <MenuItem icon={<EditIcon />} onClick={handleEditReportTitle}>
            Rename Report
          </MenuItem>
        )}
        {canRenameReport && <Divider />}
        <MenuItem icon={<TrashIcon />} onClick={openModal} type="error">
          {isMeetingOwner ? "Delete" : "Remove"} Report
        </MenuItem>
      </Menu>
      {isMeetingOwner ? (
        <DeleteMeetingReportConfirmationModal
          loading={deleteReport.isPending}
          open={confirmationModalOpen}
          onClose={closeModal}
          onConfirm={handleDeleteReport}
        />
      ) : (
        <RemoveMeetingReportConfirmationModal
          loading={removeReport.isPending}
          open={confirmationModalOpen}
          onClose={closeModal}
          onConfirm={handleRemoveReport}
        />
      )}
    </>
  );
};

export default ReportActionsMenu;
